import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, tap, map, catchError } from "rxjs";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";
import { DEFAULT_TYPE_USER } from "../shared/constants/app.constants";
import { BaseService } from "../services/shared/base.service";

interface LoginResponse {
  message: string;
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    color: string;
  };
}

/**
 * AuthService — token storage strategy (AUD-007).
 *
 * - Access token: kept ONLY in memory (private field). Not exposed to JS via
 *   any storage API.
 * - Refresh token: kept ONLY in an httpOnly cookie set by ms-identify-service.
 *   Not accessible to JavaScript at all.
 * - User object (non-sensitive UI state) is persisted in sessionStorage so a
 *   page reload still works without leaking tokens. On reload, the access
 *   token is restored by exchanging the httpOnly refresh cookie via
 *   bootstrapSession() (called from the auth guard).
 */
@Injectable({
  providedIn: "root",
})
export class AuthService extends BaseService {
  private apiUrl = environment.apiUrlMSIdentifyService + "/auth/login";
  private refreshUrl = environment.apiUrlMSIdentifyService + "/auth/refresh";

  /** In-memory access token. Lost on full page reload, restored via refresh cookie. */
  private accessToken: string | null = null;

  constructor(http: HttpClient, private router: Router) {
    super(http);
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const body = { email, password, typeUser: DEFAULT_TYPE_USER };
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    // withCredentials: true so the browser stores the httpOnly refresh cookie
    // emitted by the backend.
    return this.http
      .post<LoginResponse>(this.apiUrl, body, { headers, withCredentials: true })
      .pipe(
        tap((response) => {
          this.accessToken = response.accessToken;
          // refreshToken in body is ignored on web; it lives in httpOnly cookie.
          sessionStorage.setItem("user", JSON.stringify(response.user));
        })
      );
  }

  logout(): Observable<any> {
    const logoutUrl = environment.apiUrlMSIdentifyService + "/auth/logout";
    return this.http.post<any>(logoutUrl, {}, { withCredentials: true }).pipe(
      tap(() => {
        this.clearSession();
        this.router.navigate(["/"]);
      })
    );
  }

  logoutLocal(): void {
    this.clearSession();
    this.router.navigate(["/"]);
  }

  private clearSession(): void {
    this.accessToken = null;
    sessionStorage.removeItem("user");
    // Cleanup legacy localStorage keys from previous builds.
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    } catch {
      /* no-op */
    }
  }

  /**
   * Refreshes the access token using the httpOnly refresh cookie.
   */
  refreshAccessToken(): Observable<{ accessToken: string; refreshToken?: string }> {
    return this.http
      .post<{ accessToken: string; refreshToken?: string }>(
        this.refreshUrl,
        {},
        { withCredentials: true }
      )
      .pipe(
        tap((response) => {
          this.accessToken = response.accessToken;
        })
      );
  }

  /**
   * Attempts to silently restore the session on app start by exchanging the
   * httpOnly refresh cookie for a fresh access token.
   */
  bootstrapSession(): Observable<boolean> {
    if (this.accessToken) {
      return of(true);
    }
    return this.refreshAccessToken().pipe(
      map((response) => !!response.accessToken),
      catchError(() => of(false))
    );
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  getCurrentUser(): any {
    const user = sessionStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  /** Backwards-compatible no-op: refresh token is now in httpOnly cookie. */
  getRefreshToken(): string | null {
    return null;
  }
}
